import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../components/Layout';
import apiClient from '../api/client';

interface PlanterContact {
  contactDate: string;
  notes?: string;
  contactType: string;
}

interface PlanterStatus {
  id: number;
  name: string;
  spouseName?: string;
  status?: string;
  stage?: string;
  city?: string;
  state?: string;
  assignedHub?: string;
  headshotImagePath?: string | null;
  contactStatus: 'contacted' | 'pending' | 'overdue' | 'needs_in_person' | 'exempt';
  lastContact: PlanterContact | null;
}

interface HubOverview {
  id: number | null;
  name: string;
  hubLeader: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  planters: PlanterStatus[];
  summary: {
    total: number;
    contacted: number;
    pending: number;
    overdue: number;
    needsInPerson: number;
    exempt: number;
  };
}

interface OverviewResponse {
  success: boolean;
  data: HubOverview[];
  cadenceInfo: string;
  frequencyDays?: number;
  periodStart?: string;
  periodEnd?: string;
}

const statusConfig = {
  contacted: { dot: 'bg-green-500', bg: 'bg-green-50', text: 'text-green-700', label: 'Contacted' },
  pending: { dot: 'bg-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700', label: 'Pending' },
  overdue: { dot: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', label: 'Overdue' },
  needs_in_person: {
    dot: 'bg-amber-500',
    bg: 'bg-amber-50',
    text: 'text-amber-800',
    label: 'In-person due',
  },
  exempt: {
    dot: 'bg-slate-400',
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    label: 'Tracking off',
  },
};

const HubContactOverview: React.FC = () => {
  const [expandedHubs, setExpandedHubs] = useState<Set<string>>(new Set());

  const { data: overviewData, isLoading } = useQuery<OverviewResponse>({
    queryKey: ['admin-hub-contact-overview'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/hub-contact-overview?include=userLink');
      return res.data;
    },
  });

  const hubs = overviewData?.data || [];
  const cadenceInfo =
    overviewData?.cadenceInfo ||
    'Cadence depends on each planter’s phase (growth, training, funding, or post-funding).';

  const hubRowKey = (hub: HubOverview) =>
    hub.id != null ? `hub-${hub.id}` : `orphan-${hub.name}`;

  const toggleHub = (key: string) => {
    setExpandedHubs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const totalStats = hubs.reduce(
    (acc, hub) => ({
      total: acc.total + hub.summary.total,
      contacted: acc.contacted + hub.summary.contacted,
      pending: acc.pending + hub.summary.pending,
      overdue: acc.overdue + hub.summary.overdue,
      needsInPerson: acc.needsInPerson + (hub.summary.needsInPerson ?? 0),
      exempt: acc.exempt + (hub.summary.exempt ?? 0),
    }),
    { total: 0, contacted: 0, pending: 0, overdue: 0, needsInPerson: 0, exempt: 0 }
  );

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Hub Contact Overview</h1>
          <p className="mt-1 text-sm text-gray-600">
            Track hub leader contact compliance with their assigned planters.
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-6">
          <h3 className="text-sm font-medium text-gray-900">Contact cadence</h3>
          <p className="text-sm text-gray-600 mt-1">{cadenceInfo}</p>
        </div>

        {/* Summary Stats */}
        {!isLoading && hubs.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white shadow rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{totalStats.total}</div>
              <div className="text-sm text-gray-500">Total Planters</div>
            </div>
            <div className="bg-green-50 shadow rounded-lg p-4 text-center border border-green-200">
              <div className="text-2xl font-bold text-green-700">{totalStats.contacted}</div>
              <div className="text-sm text-green-600">Contacted</div>
            </div>
            <div className="bg-yellow-50 shadow rounded-lg p-4 text-center border border-yellow-200">
              <div className="text-2xl font-bold text-yellow-700">{totalStats.pending}</div>
              <div className="text-sm text-yellow-600">Pending</div>
            </div>
            <div className="bg-amber-50 shadow rounded-lg p-4 text-center border border-amber-200">
              <div className="text-2xl font-bold text-amber-900">{totalStats.needsInPerson}</div>
              <div className="text-sm text-amber-800">In-person due</div>
            </div>
            <div className="bg-red-50 shadow rounded-lg p-4 text-center border border-red-200">
              <div className="text-2xl font-bold text-red-700">{totalStats.overdue}</div>
              <div className="text-sm text-red-600">Overdue</div>
            </div>
            <div className="bg-slate-50 shadow rounded-lg p-4 text-center border border-slate-200">
              <div className="text-2xl font-bold text-slate-700">{totalStats.exempt}</div>
              <div className="text-sm text-slate-600">Tracking off</div>
            </div>
          </div>
        )}

        {/* Hub Cards */}
        {isLoading ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Loading contact data...</p>
          </div>
        ) : hubs.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
            No active hubs found.
          </div>
        ) : (
          <div className="space-y-4">
            {hubs.map((hub) => {
              const rowKey = hubRowKey(hub);
              const expanded = expandedHubs.has(rowKey);
              const pct = hub.summary.total > 0
                ? Math.round((hub.summary.contacted / hub.summary.total) * 100)
                : 0;
              return (
                <div key={rowKey} className="bg-white shadow rounded-lg overflow-hidden">
                  {/* Hub header */}
                  <button
                    onClick={() => toggleHub(rowKey)}
                    className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="text-left min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate">{hub.name}</h3>
                        <p className="text-sm text-gray-500 truncate">
                          {hub.hubLeader
                            ? `${hub.hubLeader.firstName} ${hub.hubLeader.lastName}`
                            : 'No hub leader assigned'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      {/* Mini status dots */}
                      <div className="hidden sm:flex items-center gap-3 text-sm flex-wrap">
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                          {hub.summary.contacted}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                          {hub.summary.pending}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                          {hub.summary.needsInPerson ?? 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                          {hub.summary.overdue}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2.5 h-2.5 rounded-full bg-slate-400"></span>
                          {hub.summary.exempt ?? 0}
                        </span>
                      </div>
                      {/* Progress bar */}
                      <div className="w-20 sm:w-32">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                          <span>{pct}%</span>
                          <span>{hub.summary.contacted}/{hub.summary.total}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              pct === 100 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Expanded planter list */}
                  {expanded && (
                    <div className="border-t border-gray-200">
                      {hub.planters.length === 0 ? (
                        <div className="px-6 py-4 text-sm text-gray-500">
                          No planters assigned to this hub.
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {hub.planters.map((planter) => {
                            const cfg = statusConfig[planter.contactStatus];
                            return (
                              <div key={planter.id} className={`px-4 sm:px-6 py-3 flex items-center justify-between ${cfg.bg}`}>
                                <div className="flex items-center gap-3 min-w-0">
                                  <span className={`flex-shrink-0 w-3 h-3 rounded-full ${cfg.dot}`}></span>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {planter.name}
                                      {planter.spouseName && (
                                        <span className="text-gray-500"> & {planter.spouseName}</span>
                                      )}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {[planter.city, planter.state].filter(Boolean).join(', ') || 'No location'}
                                      {planter.lastContact && (
                                        <> &middot; Last: {formatDate(planter.lastContact.contactDate)}</>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text} border ${
                                  planter.contactStatus === 'contacted' ? 'border-green-300'
                                    : planter.contactStatus === 'pending' ? 'border-yellow-300'
                                    : planter.contactStatus === 'needs_in_person' ? 'border-amber-300'
                                    : planter.contactStatus === 'exempt' ? 'border-slate-300'
                                    : 'border-red-300'
                                }`}>
                                  {cfg.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HubContactOverview;
